import React from 'react';
import { PenLine, BookOpen, Share2, Heart, Users, GraduationCap, Home, Building2, Calendar, Phone, Mail, Megaphone, AlertTriangle } from 'lucide-react';

const PayEquityProof = () => {
  const benefits = [
    { icon: Building2, text: "Programs are more stable" },
    { icon: Users, text: "Educators remain in the field longer" },
    { icon: GraduationCap, text: "Children experience stronger learning environments" },
    { icon: Home, text: "Families have more reliable care" },
    { icon: Heart, text: "Communities thrive" }
  ];

  return (
    <div className="min-h-screen bg-white p-6" id="proof-content" role="main">
      {/* Proof Header */}
      <div className="text-center mb-4 pb-3 border-b-2 border-slate-200">
        <p className="text-sm text-slate-500 uppercase tracking-wider">Website Popup Proof • DC Early Learning Collaborative</p>
        <p className="text-xs text-slate-400 mt-1">For Team Review</p>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#079897] via-[#0A2037] to-[#079897] p-5">
          <div className="text-center text-white">
            <span className="inline-block px-3 py-1 mb-2 text-xs font-semibold tracking-wider uppercase bg-white/20 rounded-full">
              Action Alert
            </span>
            <h1 className="text-xl md:text-2xl font-bold" style={{ fontFamily: 'Roboto, sans-serif' }}>
              Support Pay Equity for Early Childhood Educators
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Intro */}
          <div className="mb-5 text-center max-w-3xl mx-auto">
            <p className="text-[#0A2037] leading-relaxed mb-2 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Early childhood educators are the backbone of our communities. They support children's development during the most critical years of life, while making it possible for families across Washington, DC to work and thrive.
            </p>
            <p className="text-[#0A2037]/80 leading-relaxed mb-2 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Yet many early childhood educators earn far less than their peers in the K–12 system, despite doing work that is just as essential.
            </p>
            <p className="text-[#0A2037] font-medium leading-relaxed text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span className="text-[#079897]">Pay equity is not just a workforce issue.</span> It is a systems issue that affects the stability of early learning programs, the well-being of educators, and the future success of children.
            </p>
          </div>

          {/* Take Action Header */}
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold text-[#0A2037] inline-flex items-center gap-2" style={{ fontFamily: 'Roboto, sans-serif' }}>
              <Heart className="w-5 h-5 text-[#079897]" />
              Take Action!
            </h2>
          </div>

          {/* URGENT ALERT */}
          <div className="mb-5 bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-red-800 mb-1 text-sm" style={{ fontFamily: 'Roboto, sans-serif' }}>
                  🚨 Take Action This Week: Call + Email the Mayor
                </h3>
                <p className="text-xs text-red-800 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <strong>DC Just Announced a Child Care Subsidy "Waitlist" beginning May 12.</strong> In reality, it is an <strong>Enrollment Freeze</strong>.
                </p>
                <p className="text-xs text-red-700 mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Children will miss out on early learning. Parents will be forced to delay returning to work, quit jobs, or leave school.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Mail className="w-3 h-3" /> Email District Leaders
                  </span>
                  <span className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    <Phone className="w-3 h-3" /> Sign Up to Call the Mayor
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Boxes Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-5">
            {/* Sign Letter */}
            <div className="bg-[#079897]/10 border-2 border-[#079897]/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-[#079897] p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                  <PenLine className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2037] mb-1 text-sm">Sign the Pay Equity Letter</h4>
                  <p className="text-xs text-[#0A2037]/80 mb-2">Add your name to our advocacy letter calling on DC policymakers to advance equitable compensation.</p>
                  <span className="inline-block bg-[#079897] text-white text-xs font-semibold px-3 py-1 rounded-full">Sign the Letter →</span>
                </div>
              </div>
            </div>

            {/* Storybook */}
            <div className="bg-[#95C73E]/10 border-2 border-[#95C73E]/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-[#95C73E] p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2037] mb-1 text-sm">Share the Educator Storybook</h4>
                  <p className="text-xs text-[#0A2037]/80 mb-2">Subscribe to get the upcoming educator storybook and share powerful stories.</p>
                  <span className="inline-block bg-[#95C73E] text-white text-xs font-semibold px-3 py-1 rounded-full">Subscribe →</span>
                </div>
              </div>
            </div>

            {/* Wednesday Walkarounds */}
            <div className="bg-[#0A2037]/10 border-2 border-[#0A2037]/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-[#0A2037] p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2037] mb-1 text-sm">Join Wednesday Council Walkarounds</h4>
                  <p className="text-xs text-[#0A2037]/80 mb-1">Sign up for a future Wednesday to join our Council walkarounds.</p>
                  <p className="text-xs text-[#0A2037]/60 mb-2 italic"><strong>Directors:</strong> Adopt a day with your staff + parent community.</p>
                  <div className="flex gap-2">
                    <span className="inline-block bg-[#0A2037] text-white text-xs font-semibold px-3 py-1 rounded-full">RSVP →</span>
                    <span className="inline-block bg-[#0A2037]/70 text-white text-xs font-semibold px-3 py-1 rounded-full">Adopt a Wednesday →</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Spread the Word */}
            <div className="bg-[#FBBD55]/10 border-2 border-[#FBBD55]/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-[#FBBD55] p-2 bg-white rounded-full shadow-sm flex-shrink-0">
                  <Share2 className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2037] mb-1 text-sm">Spread the Word</h4>
                  <p className="text-xs text-[#0A2037]/80 mb-2">Download our social media toolkit or share about pay equity using your own voice.</p>
                  <span className="inline-block bg-[#FBBD55] text-[#0A2037] text-xs font-semibold px-3 py-1 rounded-full">Get Toolkit →</span>
                </div>
              </div>
            </div>
          </div>

          {/* Events Section */}
          <div className="mb-5">
            <div className="text-center mb-3">
              <h3 className="text-base font-bold text-[#0A2037] inline-flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#079897]" /> Upcoming Events
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {/* March 23 */}
              <div className="bg-[#079897]/5 border border-[#079897]/20 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-[#079897] text-white text-xs font-bold px-2 py-0.5 rounded">MAR 23</span>
                  <span className="text-xs text-[#079897] font-semibold">6:45 PM</span>
                </div>
                <h4 className="font-bold text-[#0A2037] text-sm mb-1">State of Child Care Town Hall</h4>
                <p className="text-xs text-[#0A2037]/70 mb-2">Join Under 3 DC for a Town Hall on changes to educator salaries and child care subsidy with OSSE and Councilmembers.</p>
                <span className="inline-block bg-[#079897] text-white text-xs font-semibold px-2 py-1 rounded-full">RSVP Today →</span>
              </div>
              {/* March 29 */}
              <div className="bg-[#0A2037]/5 border border-[#0A2037]/20 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-[#0A2037] text-white text-xs font-bold px-2 py-0.5 rounded">MAR 29</span>
                  <span className="text-xs text-[#0A2037] font-semibold">In-Person</span>
                </div>
                <h4 className="font-bold text-[#0A2037] text-sm mb-1">DC Candidate Forum</h4>
                <p className="text-xs text-[#0A2037]/70 mb-2">Hear where candidates stand on healthcare, child care, paid family leave, and workers' rights.</p>
                <div className="flex gap-2">
                  <span className="inline-block bg-[#0A2037] text-white text-xs font-semibold px-2 py-1 rounded-full">Sign Up →</span>
                  <span className="inline-block border border-[#0A2037] text-[#0A2037] text-xs font-semibold px-2 py-1 rounded-full">Get Flyers →</span>
                </div>
              </div>
            </div>
          </div>

          {/* Protest Section */}
          <div className="mb-5 bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 p-2 rounded-full flex-shrink-0">
                <Megaphone className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h4 className="font-bold text-amber-900 mb-1 text-sm">✊ PROTEST: Rolling Closures (Early Dismissals)</h4>
                <p className="text-xs text-amber-800 mb-2">Sign up with <strong>SPACEs in Action</strong>! These strategic actions make visible what policymakers ignore: when child care is unstable, the entire District is unstable.</p>
                <span className="inline-block bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Sign Up to Learn More →</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-br from-[#0A2037] to-[#152d4a] rounded-xl p-4 text-white">
            <h4 className="text-sm font-bold mb-3 text-center">When early childhood educators are fairly compensated:</h4>
            <div className="grid grid-cols-5 gap-2 mb-2">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="bg-white/10 rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-1">
                      <Icon className="w-4 h-4 text-[#95C73E]" />
                    </div>
                    <p className="text-[9px] text-white/90 leading-tight">{benefit.text}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-[#95C73E] font-semibold text-xs">Pay equity strengthens the entire early childhood system.</p>
          </div>
        </div>
      </div>

      {/* Proof Footer */}
      <div className="text-center mt-4 pt-3 border-t-2 border-slate-200">
        <p className="text-xs text-slate-400">DCELC • DC Early Learning Collaborative • www.dcelc.org</p>
      </div>
    </div>
  );
};

export default PayEquityProof;
